// ===========================================================================
// Module C — Medication Adherence Manager · Service layer
//
// Hoy lee polypharmacy.json de los dumps diarios de DataDashCareManager.
// PDC, refill gaps, clase terapéutica y barreras NO existen en el dump
// (requieren feed de farmacia/claims Rx): se derivan DETERMINÍSTICAMENTE
// desde cipId/condiciones para que la UI sea estable. SIMULADO hasta que el
// backend exponga el schema pharmacy (TAB_ADHERENCE / TAB_REFILL_GAPS).
// ===========================================================================

import { ADHERENCE_BARRIERS, PDC_THRESHOLD } from "../common/constants";

/** Selección determinística desde cipId. */
const pick = (cipId, arr) => arr[Number(cipId) % arr.length];

/**
 * Clase terapéutica primaria: mapeada desde las condiciones reales del
 * paciente cuando es posible; fallback determinístico por cipId.
 */
function deriveTherapeuticClass(p) {
  const conditions = (p.conditions || "").toLowerCase();
  if (conditions.includes("diabetes")) return "Diabetes";
  if (conditions.includes("hypertension")) return "Hypertension (RASA)";
  if (conditions.includes("hyperlipidemia")) return "Statins";
  return pick(p.cipId, ["Diabetes", "Hypertension (RASA)", "Statins"]);
}

/** PDC simulado: 45-90% determinístico (cipId % 46 + 45). */
function derivePdc(cipId) {
  return 45 + (Number(cipId) % 46);
}

/** Refill gaps abiertos — SIMULADO (cipId % 4). */
function deriveRefillGaps(cipId, pdc) {
  if (pdc >= PDC_THRESHOLD) return 0;
  return 1 + (Number(cipId) % 3);
}

/** Barrera principal reportada — SIMULADA (cipId % barreras). */
function deriveBarrier(cipId) {
  return pick(cipId, ADHERENCE_BARRIERS);
}

/** Flags de adherencia calculados a partir de dump + derivados. */
function deriveFlags(p, pdc, refillGaps) {
  const flags = [];
  if (pdc < PDC_THRESHOLD) flags.push("BELOW_PDC");
  if (refillGaps > 0) flags.push("REFILL_GAP");
  if (p.recentLabs === "N") flags.push("NO_RECENT_LABS");
  if ((p.medications ?? 0) >= 8) flags.push("HIGH_PILL_BURDEN");
  if (/9 Months|12\+ Months/.test(p.lastVisit || "")) flags.push("OVERDUE_VISIT");
  return flags;
}

/**
 * Obtiene la cola de adherencia/polifarmacia de una location, enriquecida con
 * PDC simulado por clase terapéutica, refill gaps y flags de adherencia.
 *
 * FUTURO ENDPOINT BACKEND: GET /api/Pharmacy/adherence?locationKey={key}
 * (respaldado por pharmacy.TAB_ADHERENCE y pharmacy.TAB_REFILL_GAPS).
 * Hoy: dump JSON local + derivación determinística simulada.
 *
 * @param {string} locationKey - Key de la location (e.g. "fort_myers")
 *
 * Ejemplo de respuesta:
 * {
 *   "stats": { "totalWith5Plus": 57, "totalWith8Plus": 15, "withoutRecentLabs": 35, ... },
 *   "patients": [
 *     {
 *       "cipId": 30000325609, "name": "PAZERA, JAMIE", "medications": 14,
 *       "recentLabs": "Y", "lastVisit": "<3 Months", "healthStatus": "At Risk",
 *       // —— derivados (simulados) ——
 *       "pdc": 64, "therapeuticClass": "Diabetes", "refillGaps": 2,
 *       "flags": ["BELOW_PDC", "REFILL_GAP", "HIGH_PILL_BURDEN"],
 *       "barrier": "Cost / copay concerns"
 *     }
 *   ]
 * }
 *
 * @returns {Promise<{stats: Object, patients: Object[]}>}
 */
export async function getAdherenceQueue(locationKey) {
  const module = await import(
    `../../../pages/Main/Temp/DataDashCareManager/${locationKey}/polypharmacy.json`
  );
  const data = module.default || module;

  const patients = (data.patients || []).map((p) => {
    const pdc = derivePdc(p.cipId);
    const refillGaps = deriveRefillGaps(p.cipId, pdc);
    return {
      ...p,
      pdc,
      therapeuticClass: deriveTherapeuticClass(p),
      refillGaps,
      flags: deriveFlags(p, pdc, refillGaps),
      barrier: pdc < PDC_THRESHOLD ? deriveBarrier(p.cipId) : null,
    };
  });

  // Cola por PDC ascendente (peor adherencia primero), luego más medicamentos
  patients.sort((a, b) => a.pdc - b.pdc || (b.medications ?? 0) - (a.medications ?? 0));

  return { stats: data.stats || {}, patients };
}
